import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BigNumber from "bignumber.js";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router";
import { useReactToPrint } from "react-to-print";
import { ReactComponent as Biofuel } from "../../../assets/images/icons/ic-biofuel.svg";
import PrimeDialog from "../../../components/dialog/PrimeDialog";
import { useEditableFlag } from "../../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../../store/messageStore";
import NumberUtil from "../../../utility/NumberUtil";
import { downloadMultipartFile } from "../../../utility/service/api-file";
import { getSubItemsByItemNo, initDataProp } from "../../../utility/service/api-service";
import {
  createCustomEmissions,
  deleteCustomEmissions,
  getCompanyPlaceList,
  getPlanOrganizationalBoundaryList,
  queryEmissionsCalList,
  updateCustomEmissions,
} from "../../../utility/service/carbon-service";
import { checkDpSuccess } from "../../../utility/service/tool-service";
import AddCustomEmission from "./AddCustomEmission";
import MainDataTable from "./MainDataTable";
import { AcctContext } from "../../../utility/useReduce/userNameReduce";

// 精確的浮點數計算函數
const preciseSubtract = (a, b) => {
  const aStr = a.toString();
  const bStr = b.toString();

  // 獲取小數位數
  const aDecimalPlaces = (aStr.split(".")[1] || "").length;
  const bDecimalPlaces = (bStr.split(".")[1] || "").length;
  const maxDecimalPlaces = Math.max(aDecimalPlaces, bDecimalPlaces);

  // 轉換為整數進行計算
  const multiplier = Math.pow(10, maxDecimalPlaces);
  const aInt = Math.round(parseFloat(aStr) * multiplier);
  const bInt = Math.round(parseFloat(bStr) * multiplier);

  // 進行整數運算
  const result = aInt - bInt;

  // 轉換回浮點數
  return (result / multiplier).toFixed(maxDecimalPlaces);
};
const tableColumnKeysCommon = {
  period: "period",
  gasName: "gasName",
  amount: "amount",
  unit: "activityDataUnit",
  carbonRate: "carbonRate",
  gasRate: "gasRate",
  gasRateUnit: "gasRateUnit",
  gasRate2: "gasRate2",
  gasRate3: "gasRate3",
  gasRate4: "gasRate4",
  gasGwp: "gasGwp",
  detailEmission: "detailEmission",
};

const tableColumnKeysByCase = {
  "1-1": {
    keys: tableColumnKeysCommon,
    excludes: ["carbonRate", "gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  "1-2": {
    keys: {
      ...tableColumnKeysCommon,
      carbonRate: "carbon_content", //含碳量
    },
    excludes: ["gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  "1-3": {
    keys: tableColumnKeysCommon,
    excludes: ["carbonRate", "gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  "1-4": {
    keys: {
      ...tableColumnKeysCommon,
      gasRate: "escape_rate", //逸散率
    },
    excludes: ["carbonRate", "gasRateUnit", "gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  "1-5": {
    keys: {
      ...tableColumnKeysCommon,
      gasRate: "usage_rate", //使用率
    },
    excludes: ["carbonRate", "gasRateUnit", "gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  "1-6": {
    keys: {
      ...tableColumnKeysCommon,
      gasRate: "leak_rate", //洩漏率
    },
    excludes: ["carbonRate", "gasRateUnit", "gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  "1-7": {
    keys: tableColumnKeysCommon,
    excludes: ["carbonRate", "gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  "1-8": {
    keys: {
      ...tableColumnKeysCommon,
      gasRate: "cod", //化學需氧量(COD)
      gasRate2: "bo", //最大甲烷產生量(Bo)
      gasRate3: "mcf", //甲烷轉換(修正)係數
      gasRate4: "combustion", //甲烷補給與燃燒量
    },
    excludes: ["carbonRate", "gasRateUnit"],
    multiRows: ["period", "amount", "unit"],
  },
  "1-9": {
    keys: {
      ...tableColumnKeysCommon,
      carbonRate: "gasProportion", //氣體比例
      gasRate: "gasDensity", //氣體密度
      gasRateUnit: "gasDensityUnit", //氣體密度單位
    },
    excludes: ["gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  "1-10": {
    keys: {
      ...tableColumnKeysCommon,
      carbonRate: "gasProportion", //氣體比例
      gasRate: "gasDensity", //氣體密度
      gasRateUnit: "gasDensityUnit", //氣體密度單位
    },
    excludes: ["gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  "1-11": {
    keys: tableColumnKeysCommon,
    excludes: ["carbonRate", "gasRate", "gasRateUnit", "gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  "2-1": {
    keys: tableColumnKeysCommon,
    excludes: ["carbonRate", "gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  "2-2": {
    keys: tableColumnKeysCommon,
    excludes: ["carbonRate", "gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  "3-1": {
    keys: tableColumnKeysCommon,
    excludes: ["carbonRate", "gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  "3-2": {
    keys: tableColumnKeysCommon,
    excludes: ["carbonRate", "gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  "4-1": {
    keys: tableColumnKeysCommon,
    excludes: ["carbonRate", "gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  "4-x": {
    keys: tableColumnKeysCommon,
    excludes: ["carbonRate", "gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
  default: {
    keys: tableColumnKeysCommon,
    excludes: ["carbonRate", "gasRate2", "gasRate3", "gasRate4"],
    multiRows: ["period", "amount", "unit"],
  },
};

const SYMBOL_CONFIG = {
  "*": { priority: 1, label: "i", color: "#6b7280" },
  "!": { priority: 2, label: "!", color: "#ef4444" },
};

const EmissionsCalculation = () => {
  const { t } = useTranslation();
  const isEditable = useEditableFlag("F10103"); // control edit by F10103
  const [expandedRows, setExpandedRows] = useState(null);
  const [cusObj, setCusObj] = useState({});
  const [cusDialog, setCusDialog] = useState(false);
  const { itemObj, setAction, action, setItemObj } = useOutletContext();
  const [emissionList, setEmissionList] = useState([]);
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const [state, dispatchUser] = useContext(AcctContext); //訊息視窗
  //變更計算目前狀態
  const [calStatus, setCalStatus] = useState("create");
  const [totalEmission, setTotalEmission] = useState("");
  const [emissionUnitList, setEmissionUnitList] = useState([]); //單位轉換
  //係數種類
  const [rateTypeList, setRateTypeList] = useState([]);
  const fileInputRef = useRef(null);
  const [bioEnergyEmission, setBioEnergyEmission] = useState(""); //生質排放量(tCO2e)
  // 列印排放量計算頁面
  const contentRef = useRef(null);
  const promiseResolveRef = useRef(null);
  const [currExpandedRows, setCurrExpandedRows] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isAllExpanded, setIsAllExpanded] = useState(false);

  const [calculationAuditFlag, setCalculationAuditFlag] = useState(false);
  const [calculationAuditLogList, setCalculationAuditLogList] = useState([]);

  const [newTableFlag, setNewTableFlag] = useState(false);
  const [planPlaceSeq, setPlanPlaceSeq] = useState(null);
  const [companyPlaceList, setCompanyPlaceList] = useState([]);

  // 簽核
  const [approveFlag, setApproveFlag] = useState(false);
  const [approveStatus, setApproveStatus] = useState("C");
  const [cusDisable, setCusDisable] = useState(true);
  const approveStatusList = [
    { label: t("approve.D"), value: "D" },
    { label: t("approve.P"), value: "P" },
    { label: t("approve.C"), value: "C" },
    { label: t("approve.RTA"), value: "RTA" },
  ];

  const getClassByCriterion = (criterion) => {
    if (criterion === "0") return "S";
    if (criterion === "9") return "G";
    return "C";
  };

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  const reactToPrintFn = useReactToPrint({
    documentTitle:
      itemObj.year +
      "_" +
      itemObj.displayCause +
      "_" +
      itemObj.companyName +
      "_" +
      itemObj.placeName +
      "_" +
      t("Emissions_calculation"),
    contentRef,
    onBeforePrint: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setCurrExpandedRows(expandedRows);
        setExpandedRows([...emissionList]);
        setIsPrinting(true);
      });
    },
    onAfterPrint: () => {
      setExpandedRows(currExpandedRows);
      setIsPrinting(false);
    },
  });

  const axios_queryEmissionsCalList = (reqBody) => {
    checkLoading(loading);
    queryEmissionsCalList(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          setTotalEmission(res?.data.RespBody?.totalEmission);
          setEmissionList(res?.data?.RespBody?.emissionList);
          setBioEnergyEmission(res?.data?.RespBody?.bioEnergyEmission);
          setApproveFlag(res?.data?.RespBody?.approveFlag);
        }
      })
      .finally(() => checkLoading(loading, false));
  };

  useEffect(() => {
    axios_queryEmissionsCalList({
      planSn: itemObj?.planSn,
      class: getClassByCriterion(itemObj?.criterion),
      planPlaceSeq: planPlaceSeq,
      approveStatus: approveStatus,
    });
  }, [itemObj?.planSn !== undefined && itemObj?.criterion !== undefined, planPlaceSeq, approveStatus]);

  // 若目前為展開全部狀態，資料更新時維持展開全部
  useEffect(() => {
    if (isAllExpanded && Array.isArray(emissionList)) {
      setExpandedRows([...emissionList]);
    }
  }, [isAllExpanded, emissionList]);

  const fetchEmissionUnitData = async () => {
    try {
      const emissionUnitResponse = await getSubItemsByItemNo("CFV_EMISSION_UNIT", 7); //類1-3
      if (!checkDpSuccess(emissionUnitResponse)) return;

      let data = emissionUnitResponse.data.RespBody.subItems;
      setEmissionUnitList(data);

      const coefficientUnitResponse = await getSubItemsByItemNo("COEFFICIENT_UNIT", 27); //類4-6
      if (checkDpSuccess(coefficientUnitResponse)) {
        setEmissionUnitList((prevData) => [...prevData, ...coefficientUnitResponse.data.RespBody.subItems]);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    //單位
    fetchEmissionUnitData();
    axios_initDataProp();
    axios_getPlanOrganizationalBoundaryList();

    //係數種類
    getSubItemsByItemNo("CFV_RATE_TYPE", 16).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        setRateTypeList(data);
      }
    });
  }, []);

  // 組織邊界場所
  const axios_getPlanOrganizationalBoundaryList = () => {
    getPlanOrganizationalBoundaryList({ planSn: itemObj?.planSn }).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.planList;

        // 找尋場所名稱
        getCompanyPlaceList().then((res) => {
          if (checkDpSuccess(res)) {
            let data2 = res.data.RespBody.companyPlaceList?.filter((item) => item.companySeq === itemObj?.companySeq)[0]
              ?.placeList;
            const placeMap = new Map();
            data2.forEach((item) => {
              placeMap.set(item.placeSeq, item.placeName);
            });
            data.forEach((plan) => {
              plan.placeName = placeMap.get(plan.placeSeq) || null;
            });
          }
        });

        setCompanyPlaceList(data);
      }
    });
  };

  const {
    register,
    control,
    getValues,
    setValue,
    watch,
    clearErrors,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {},
    mode: "all",
  });

  const cusCalculation = (data) => {
    setCusObj(data);
    setCusDialog(true);
    const isDraft = data?.status === "D";
    const isRTA = data?.status === "RTA" && (data?.applyAdmin ?? false);
    const isAdmin = state?.isAdmin === "Y" && itemObj?.companySeq === state?.companySeq && data?.status === "C";

    setCusDisable(isDraft || isRTA || isAdmin);
    clearErrors(["customEmission", "customDesc"]);
    if (data?.hasCustom === "N") {
      //如果customEmission值是空的表是建立 v0.75改成 hasCustom (Y更新/N 建立)
      setCalStatus("create");
      setValue("customRateType", "5");
      if (data?.dataList?.length > 0) {
        setValue("gasRateUnit1", data?.dataList?.[0]?.detailDataList?.[0]?.gasRateUnit);
        setValue("gasRateUnit2", data?.dataList?.[0]?.detailDataList?.[1]?.gasRateUnit);
        setValue("gasRateUnit3", data?.dataList?.[0]?.detailDataList?.[2]?.gasRateUnit);
      }
    } else {
      //否則更新
      setCalStatus("update");
      if (data?.dataList?.length > 0) {
        if (data?.detailCase === "1-5") {
          setValue("customRate1", preciseSubtract(1, data?.dataList?.[0]?.detailDataList?.[0]?.gasRate));
        } else {
          setValue("customRate1", data?.dataList?.[0]?.detailDataList?.[0]?.gasRate);
        }
        setValue("gasRateUnit1", data?.dataList?.[0]?.detailDataList?.[0]?.gasRateUnit);
        if (data?.detailCase === "1-1") {
          setValue("customRate2", data?.dataList?.[0]?.detailDataList?.[1]?.gasRate);
          setValue("customRate3", data?.dataList?.[0]?.detailDataList?.[2]?.gasRate);
          setValue("gasRateUnit2", data?.dataList?.[0]?.detailDataList?.[1]?.gasRateUnit);
          setValue("gasRateUnit3", data?.dataList?.[0]?.detailDataList?.[2]?.gasRateUnit);
          setValue("gwpValue0", data?.dataList?.[0]?.detailDataList?.[0]?.gasGwp);
          setValue("gwpValue1", data?.dataList?.[0]?.detailDataList?.[1]?.gasGwp);
          setValue("gwpValue2", data?.dataList?.[0]?.detailDataList?.[2]?.gasGwp);
        } else if (data?.detailCase === "1-2" || data?.detailCase === "1-9") {
          setValue("carbonValue", data?.dataList?.[0]?.detailDataList?.[0]?.carbonRate);
          // setValue("gwpValue", data?.dataList?.[0]?.detailDataList?.[0]?.gasGwp);
        } else if (
          data?.detailCase === "1-3" ||
          data?.detailCase === "1-4" ||
          data?.detailCase === "1-5" ||
          data?.detailCase === "1-6" ||
          data?.detailCase === "1-7" ||
          data?.detailCase === "1-11"
        ) {
          // setValue("gwpValue", data?.dataList?.[0]?.detailDataList?.[0]?.gasGwp);
        } else if (data?.detailCase === "1-8") {
          setValue("gasRate2", data?.dataList?.[0]?.detailDataList?.[0]?.gasRate2);
          setValue("gasRate3", data?.dataList?.[0]?.detailDataList?.[0]?.gasRate3);
          setValue("gasRate4", data?.dataList?.[0]?.detailDataList?.[0]?.gasRate4);
          // setValue("gwpValue", data?.dataList?.[0]?.detailDataList?.[0]?.gasGwp);
        } else if (data?.detailCase === "1-10") {
          setValue("carbonValue0", data?.dataList?.[0]?.detailDataList?.[0]?.carbonRate);
          setValue("gwpValue0", data?.dataList?.[0]?.detailDataList?.[0]?.gasGwp);
          setValue("customRate2", data?.dataList?.[0]?.detailDataList?.[1]?.gasRate);
          setValue("carbonValue1", data?.dataList?.[0]?.detailDataList?.[1]?.carbonRate);
          setValue("gwpValue1", data?.dataList?.[0]?.detailDataList?.[1]?.gasGwp);
        }
        setValue("gwpValue", data?.dataList?.[0]?.detailDataList?.[0]?.gasGwp);
      }
      setValue("customRateSource", data?.customRateSource);
      setValue("customRateType", data?.customRateType);
      setValue("customEmission", data?.customEmission);
      setValue("customDesc", data?.customDesc);
      setValue("fileName", data?.fileName);
      setValue("fileExtension", data?.fileExtension);

      if (data?.fileId !== null) {
        //如果有檔案 將檔案下載改成file存給 customFile
        downloadMultipartFile({
          fileId: data?.fileId,
        }).then((res) => {
          if (res) {
            const binaryData = res.data;
            const blob = new Blob([binaryData], { type: "application/octet-stream" });
            const file = new File([blob], `${data?.fileName}.${data?.fileExtension}`, { type: blob.type });
            setValue("customFile", file);
          }
        });
      }
    }
  };

  const confirmCusReg = () => {
    //v0.75版
    checkLoading(loading);
    let customFile =
      getValues("customFile") !== undefined && getValues("customFile") !== "" ? getValues("customFile") : null;
    let fileExtension =
      getValues("fileExtension") !== undefined && getValues("fileExtension") !== "" ? getValues("fileExtension") : null;
    let fileName = getValues("fileName") !== undefined && getValues("fileName") !== "" ? getValues("fileName") : null;

    const formData = new FormData();
    formData.append("identifySeq", cusObj?.identifySeq);

    if (getValues("customRate1")) {
      formData.append("customRate1", getValues("customRate1"));
    }
    if (getValues("customRate2")) {
      formData.append("customRate2", getValues("customRate2") === undefined ? "" : getValues("customRate2"));
    }
    if (getValues("customRate3")) {
      formData.append("customRate3", getValues("customRate3") === undefined ? "" : getValues("customRate3"));
    }
    // Not needed temporarily
    // if(getValues("customEmission")){
    //     formData.append("customEmission", getValues("customEmission"));
    // }

    if (cusObj?.detailCase === "1-1") {
      let case1_1_1 = { gwpValue: getValues("gwpValue0") };
      let case1_1_2 = { gwpValue: getValues("gwpValue1") };
      let case1_1_3 = { gwpValue: getValues("gwpValue2") };
      formData.append("customRateDetail1", JSON.stringify(case1_1_1));
      formData.append("customRateDetail2", JSON.stringify(case1_1_2));
      formData.append("customRateDetail3", JSON.stringify(case1_1_3));
    }

    if (cusObj?.detailCase === "1-2" || cusObj?.detailCase === "1-9") {
      let case1_2 = { carbonValue: getValues("carbonValue"), gwpValue: getValues("gwpValue") };
      formData.append("customRateDetail1", JSON.stringify(case1_2));
    }

    if (
      cusObj?.detailCase === "1-3" ||
      cusObj?.detailCase === "1-4" ||
      cusObj?.detailCase === "1-5" ||
      cusObj?.detailCase === "1-6" ||
      cusObj?.detailCase === "1-7" ||
      cusObj?.detailCase === "1-11"
    ) {
      let caseGwp = { gwpValue: getValues("gwpValue") };
      formData.append("customRateDetail1", JSON.stringify(caseGwp));
    }

    if (cusObj?.detailCase === "1-8") {
      let case1_8 = {
        gasRate2: !getValues("gasRate2") ? "" : getValues("gasRate2"),
        gasRate3: !getValues("gasRate3") ? "" : getValues("gasRate3"),
        gasRate4: !getValues("gasRate4") ? "" : getValues("gasRate4"),
        gwpValue: !getValues("gwpValue") ? "" : getValues("gwpValue"),
      };
      formData.append("customRateDetail1", JSON.stringify(case1_8));
    }

    if (cusObj?.detailCase === "1-10") {
      let case1_10_1 = {
        carbonValue: getValues("carbonValue0") === undefined ? "" : getValues("carbonValue0"),
        gwpValue: getValues("gwpValue0") === undefined ? "" : getValues("gwpValue0"),
      };
      let case1_10_2 = {
        carbonValue: getValues("carbonValue1") === undefined ? "" : getValues("carbonValue1"),
        gwpValue: getValues("gwpValue1") === undefined ? "" : getValues("gwpValue1"),
      };
      formData.append("customRateDetail1", JSON.stringify(case1_10_1));
      formData.append("customRateDetail2", JSON.stringify(case1_10_2));
    }

    if (!formData.has("customRateDetail1") && getValues("gwpValue")) {
      let case1_1_1 = { gwpValue: getValues("gwpValue") };
      formData.append("customRateDetail1", JSON.stringify(case1_1_1));
    }

    formData.append("customRateSource", getValues("customRateSource"));
    formData.append("customRateType", getValues("customRateType"));
    formData.append("customDesc", getValues("customDesc"));

    if (calStatus === "create") {
      formData.append("customFile", getValues("customFile"));
      formData.append("fileName", getValues("fileName"));
      formData.append("fileExtension", getValues("fileExtension"));

      createCustomEmissions(formData)
        .then((res) => {
          if (checkDpSuccess(res)) {
            setCusDialog(false);
            handleSuccessMessage(dispatch, t("button.add") + " " + t("message.success"));
            axios_queryEmissionsCalList({
              planSn: itemObj?.planSn,
              class: getClassByCriterion(itemObj?.criterion),
              planPlaceSeq: planPlaceSeq,
              approveStatus: approveStatus,
            });
            handleResetCusData(); //新增成功後清除表單文字
          }
        })
        .finally((fin) => {
          checkLoading(loading, false);
        });
    } else {
      //更新手動計算
      //圖片相關參數 編輯狀態
      if (calStatus === "update") {
        //有fileId 但沒有圖片表示要刪除 只傳 fileId 給後端 (刪除圖片)
        if (cusObj?.fileId && customFile === null) {
          formData.append("fileId", cusObj?.fileId);
          // console.log("刪除圖片");
        } else if (cusObj?.fileId === null && customFile !== null) {
          //如果沒有fileId 但有file 表示要新增file
          formData.append("customFile", customFile);
          formData.append("fileName", fileName);
          formData.append("fileExtension", fileExtension);
          // console.log("新增file");
        } else if (customFile !== null && cusObj?.fileName !== fileName && cusObj?.fileId !== null) {
          //如果有圖片 但是檔名不同表示要更新圖片
          formData.append("fileId", cusObj?.fileId);
          formData.append("customFile", customFile);
          formData.append("fileName", fileName);
          formData.append("fileExtension", fileExtension);
          // console.log("更新file");
        }
      }

      updateCustomEmissions(formData)
        .then((res) => {
          if (checkDpSuccess(res)) {
            setCusDialog(false);
            handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
            axios_queryEmissionsCalList({
              planSn: itemObj?.planSn,
              class: getClassByCriterion(itemObj?.criterion),
              planPlaceSeq: planPlaceSeq,
              approveStatus: approveStatus,
            });
            handleResetCusData(); //新增成功後清除表單文字
          }
        })
        .finally((fin) => {
          checkLoading(loading, false);
        });
    }
  };

  const handleDelCusEmission = () => {
    //刪除手動排放量計算
    deleteCustomEmissions({
      identifySeq: cusObj?.identifySeq,
    }).then((res) => {
      if (checkDpSuccess(res)) {
        handleSuccessMessage(dispatch, t("button.delete") + " " + t("message.success"));
        setCusDialog(false);
        axios_queryEmissionsCalList({
          planSn: itemObj?.planSn,
          class: getClassByCriterion(itemObj?.criterion),
          planPlaceSeq: planPlaceSeq,
          approveStatus: approveStatus,
        });
        handleResetCusData(); //新增成功後清除表單文字
      }
    });
  };

  const handleResetCusData = () => {
    setValue("customRate1", "");
    setValue("customRate2", "");
    setValue("customRate3", "");
    setValue("customRateSource", "");
    setValue("customRateType", "");
    setValue("customEmission", "");
    setValue("customDesc", "");
    setValue("customFile", "");
    setValue("fileName", "");
    setValue("fileExtension", "");
    //清除單位
    setValue("gasRateUnit1", "");
    setValue("gasRateUnit2", "");
    setValue("gasRateUnit3", "");

    // 新增欄位
    setValue("gasRate2", "");
    setValue("gasRate3", "");
    setValue("gasRate4", "");
    setValue("gwpValue", "");
    setValue("carbonValue", "");

    setValue("carbonValue0", "");
    setValue("carbonValue1", "");
    setValue("gwpValue0", "");
    setValue("gwpValue1", "");
    setValue("gwpValue2", "");
    clearErrors();
  };

  const rowExpansionTemplate = (tableData) => {
    let columns = [];
    const caseKey = tableData?.detailCase ?? "default"; // 如果tableData?.detailCase為undefined或null，使用'default'

    const columnsData = tableColumnKeysByCase[caseKey] || tableColumnKeysByCase["default"];

    if (columnsData) {
      columns = Object?.keys(columnsData?.keys)?.filter((key) => !columnsData?.excludes?.includes(key));
    }
    const colBody = ({ field, rowData, colIndex, rowIndex }) => {
      const useMultiRows = columnsData.multiRows.includes(field);
      const subRowsCount = rowData?.detailDataList?.length;
      const numberFields = ["amount", "detailEmission", "gasGwp", "gasRate", "carbonRate"];
      const cellKey = `cell-${rowIndex}-${field}-${colIndex}`;

      if (useMultiRows) {
        return (
          <div
            key={cellKey}
            className="grid-table-col"
            style={{
              gridArea: `span ${subRowsCount} / ${colIndex + 1}`,
            }}
          >
            <span
              style={{
                textAlign: numberFields.includes(field) ? "right" : "center",
                width: "100%",
              }}
            >
              {tableData[field] ??
                (numberFields.includes(field) ? NumberUtil.formatWithCommas(rowData[field]) : rowData[field])}
            </span>
          </div>
        );
      }

      if (subRowsCount > 0) {
        return rowData?.detailDataList?.map((detail, i) => {
          const hasError =
            field === "detailEmission" && Array.isArray(detail["errorMsg"]) && detail["errorMsg"].length > 0;

          // 取得所有 errorSymbol
          const allErrorSymbols = hasError ? detail["errorMsg"].map((msg) => msg.errorSymbol) : [];

          // 判斷最高優先度的 symbol
          const highestPrioritySymbol = allErrorSymbols.reduce((prev, curr) => {
            const prevPriority = SYMBOL_CONFIG[prev]?.priority || Infinity;
            const currPriority = SYMBOL_CONFIG[curr]?.priority || Infinity;
            return currPriority < prevPriority ? curr : prev;
          }, "!");

          const { color, label } = SYMBOL_CONFIG[highestPrioritySymbol] || {
            color: "#ef4444",
            label: "!",
          };

          return (
            <div
              className="grid-table-excision-col"
              key={`${cellKey}-sub-${i}`}
              style={{
                gridArea: `${2 + i + rowIndex * subRowsCount} / ${colIndex + 1}`,
              }}
            >
              {/* 如果detailEmission 出現的文字為 ! 表示計算有誤 給 紅色驚嘆號!!!!!! */}
              {/* 如果detailEmission 出現的文字為 * 表示非七大氣體不列入計算 */}
              {field === "detailEmission" && Array.isArray(detail["errorMsg"]) && detail["errorMsg"].length > 0 ? (
                <>
                  <Tooltip target=".form-exclamation" position="top" />
                  <span
                    className="form-exclamation"
                    data-pr-tooltip={
                      Array.isArray(detail["errorMsg"])
                        ? detail["errorMsg"].map((item) => item.errorMsg).join("\n")
                        : detail["errorMsg"]
                    }
                    style={{
                      textAlign: numberFields.includes(field) ? "right" : "center",
                      width: "100%",
                      "--symbol": `"${label}"`,
                      "--bg-color": color,
                    }}
                  >
                    {detail[field]?.replace("!", "")}
                    {tableData?.isBiomass === "Y" && detail?.gasName?.includes("CO2") && (
                      <Biofuel className="text-green-700" />
                    )}
                  </span>
                </>
              ) : (
                <span
                  style={{
                    textAlign: numberFields.includes(field) ? "right" : "center",
                    width: "100%",
                  }}
                >
                  {numberFields.includes(field) ? NumberUtil.formatWithCommas(detail[field]) : detail[field]}
                  {tableData?.isBiomass === "Y" && detail?.gasName?.includes("CO2") && field === "detailEmission" && (
                    <Biofuel className="text-green-700" />
                  )}
                </span>
              )}
              {/* <span>{detail?.[field]}</span> */}
            </div>
          );
        });
      }
    };
    return (
      <div
        className="grid-table"
        style={{
          "--grid-column": `${columns?.length}`,
          "--body-font-size": "0.875rem",
          marginLeft: "60px",
        }}
      >
        {columns?.map((field, index) => (
          <div className="grid-table-header" key={`${field}_${JSON?.stringify(tableData?.identifySeq)}_${index}`}>
            <span
              style={{
                textAlign: "center",
                width: "100%",
              }}
            >
              {t(columnsData?.keys[field])}
            </span>
          </div>
        ))}
        {tableData?.dataList?.map((rowData, rowIndex) => {
          return columns?.map((field, colIndex) => colBody({ field, rowData, rowIndex, colIndex }));
        })}
      </div>
    );

    // prime datable 寫法，先註解掉
    // const rowBody = (rowData, rowOptions) => {
    //     const field = rowOptions.field;
    //     const useMultiRows = columnsData.multiRows.includes(field);

    //     if (useMultiRows) {
    //         return rowData[field];
    //     }

    //     return rowData.detailDataList.map(item => {
    //         return <div className="row-expansion">{item[field]}</div>
    //     })
    // }

    // return <div className="pl-3">
    //     <DataTable
    //         showGridlines
    //         stripedRows
    //         value={tableData.dataList}>
    //         {columns.map(key => {
    //             return <Column
    //                 key={key}
    //                 field={key}
    //                 header={t(columnsData.keys[key])}
    //                 body={rowBody}
    //             ></Column>
    //         })}
    //     </DataTable>
    // </div>
  };

  const groupTemplate = (subRows, groupValues) => {
    const map = new Map();

    subRows.forEach((row) => {
      row?.original?.dataList?.forEach((item) => {
        item?.detailDataList?.forEach((detail) => {
          if (!detail) return;

          // key 用 gasName + gasRate
          const key = `${detail.gasName}`;

          // 先把 detailEmission 轉字串並去掉非數字字元（保留小數點和負號）
          let emissionStr = String(detail.detailEmission || "0").replace(/[^\d.-]/g, "");
          if (!emissionStr) emissionStr = "0";

          const emission = new BigNumber(emissionStr);

          if (!map.has(key)) {
            map.set(key, {
              gasName: detail.gasName,
              totalEmission: emission, // BigNumber
            });
          } else {
            const existing = map.get(key);
            existing.totalEmission = existing.totalEmission.plus(emission);
          }
        });
      });
    });

    // 計算要顯示幾組
    const items = Array.from(map.values());
    const displayCount = Math.min(items.length, 3); // 最多三組
    const totalColumns = displayCount * 2; // 每組兩個格子

    // 動態生成 header
    const header = [];
    for (let i = 0; i < displayCount; i++) {
      header.push(t("gasName"), t("emissions"));
    }

    return (
      <>
        <div className="p-2" style={{ fontWeight: 600 }}>
          {groupValues}
          {t("gasCodeSubtotal")}:
        </div>
        <div
          className="grid-table"
          style={{
            "--grid-column": `${totalColumns}`,
            "--body-font-size": "0.875rem",
            marginLeft: "60px",
          }}
        >
          {header.map((header, index) => (
            <div className="grid-table-header" key={`${header}_${index}`}>
              <span
                style={{
                  textAlign: "center",
                  width: "100%",
                }}
              >
                {header}
              </span>
            </div>
          ))}

          {Array.from(map.values()).map((item, index) => (
            <React.Fragment key={item.gasName}>
              {/* gasName */}
              <div
                className="grid-table-col"
                style={{
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                  wordBreak: "breakWord",
                }}
              >
                {item.gasName}
              </div>
              {/* totalEmission */}
              <div
                className="grid-table-col"
                style={{
                  width: "100%",
                  textAlign: "right",
                }}
              >
                <span
                  style={{
                    textAlign: "right",
                    width: "100%",
                  }}
                >
                  {NumberUtil.formatWithCommas(item.totalEmission, 0, 3)}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </>
    );
  };

  const allowExpansion = (rowData, props) => {
    // 如果 detailCase 為 null 也不顯示 展開按鈕 + 如果 detailDataList 為空也不顯示展開按鈕
    return rowData?.dataList?.length > 0 && rowData?.detailCase !== null;
  };

  const actionBodyTemplate = (rowData, props) => {
    const canApproveAction = approveFlag && rowData?.hasCustom === "N" && rowData?.status === "C";
    const isAdmin = state?.isAdmin === "Y" && itemObj?.companySeq === state?.companySeq && rowData?.status === "C";

    if (rowData) {
      return (
        <>
          {/* dataList 有資料才可以填寫變更計算 2024/06/19 */}
          {(itemObj.planType === "0" || itemObj.planType === "1") &&
            Array.isArray(rowData?.dataList) &&
            rowData?.dataList !== null && (
              <Button
                label={rowData?.detailCase}
                className="p-button-icon-only"
                tooltip={t("custom_calculation")}
                tooltipOptions={{ position: "top" }}
                rounded
                text
                severity="secondary"
                size="small"
                disabled={(canApproveAction && !isAdmin) || !isEditable}
                onClick={() => {
                  cusCalculation(rowData);
                }}
              >
                <FontAwesomeIcon icon="fa-solid fa-calculator" />
              </Button>
            )}
          {Array.isArray(rowData?.calculationAuditLogList) &&
            rowData?.calculationAuditLogList !== null &&
            rowData.calculationAuditLogList.length > 0 && (
              <Button
                icon="pi pi-history"
                label={rowData?.detailCase}
                className="p-button-icon-only"
                tooltip={t("calculationProcess")}
                tooltipOptions={{ position: "top" }}
                rounded
                text
                severity="secondary"
                size="small"
                onClick={() => {
                  handleCalculationAuditLog(rowData);
                }}
              />
            )}
        </>
      );
    }
  };

  //處理有!出現的值
  const subTotalEmissionTemplate = (rowData, props) => {
    if (!rowData) return null;

    const detailList = rowData?.dataList?.[0]?.detailDataList || [];
    const hasError = detailList.some((d) => Array.isArray(d.errorMsg) && d.errorMsg.length > 0);
    // 將每個 detailDataDto 的 gasName + 所有錯誤訊息組合起來
    const allErrorMsgs = detailList.flatMap((d) =>
      (d.errorMsg || []).map((msg) => (d.gasName ? `${d.gasName}: ${msg.errorMsg}` : msg.errorMsg)),
    );

    const allErrorSymbols = detailList.flatMap((d) => (d.errorMsg || []).map((msg) => msg.errorSymbol));

    const highestPrioritySymbol = allErrorSymbols.reduce((prev, curr) => {
      const prevPriority = SYMBOL_CONFIG[prev]?.priority || Infinity;
      const currPriority = SYMBOL_CONFIG[curr]?.priority || Infinity;
      return currPriority < prevPriority ? curr : prev;
    }, "!");

    const { label, color } = SYMBOL_CONFIG[highestPrioritySymbol] || { color: "#ef4444" };

    return hasError ? (
      <>
        <span
          className="form-exclamation"
          data-pr-tooltip={allErrorMsgs.join("\n")}
          style={{
            "--symbol": `"${label}"`,
            "--bg-color": color,
          }}
        >
          {NumberUtil.formatWithCommas(rowData.subtotalEmission?.replace(/[!*]/g, ""))}
        </span>
        <Tooltip target=".form-exclamation" position="top" />
        <span />
      </>
    ) : (
      <span>{NumberUtil.formatWithCommas(rowData?.subtotalEmission?.replace(/[!*]/g, ""))}</span>
    );
  };

  const formatWithCommastTemplate = (value) => {
    return <>{NumberUtil.formatWithCommas(value)}</>;
  };

  const handleTotalEmission = (totalEmission, fileName) => {
    if (totalEmission?.includes("!")) {
      const indicesWithError = emissionList
        .map((item, index) => ({ item, index }))
        .filter(({ item }) =>
          item?.dataList?.some((data) =>
            data.detailDataList?.some((detail) => {
              const errorField = fileName === "totalEmission" ? detail.errorMsg : detail.errorBiomassMsg;
              return Array.isArray(errorField) && errorField.length > 0;
            }),
          ),
        )
        .map(({ index }) => index + 1);
      return (
        <>
          <Tooltip target=".form-exclamation" position="top" />
          <span />
          <span
            className="form-exclamation"
            data-pr-tooltip={t("dataWithErrorIndex", { indices: indicesWithError.join(", ") })}
          >
            {NumberUtil.formatWithCommas(totalEmission?.replace("!", ""))}
          </span>
        </>
      );
    } else if (totalEmission?.includes("*")) {
      return (
        <>
          <Tooltip target=".form-excluded" position="top" />
          <span />
          <span className="form-excluded">{NumberUtil.formatWithCommas(totalEmission?.replace("*", ""))}</span>
        </>
      );
    } else {
      return NumberUtil.formatWithCommas(totalEmission);
    }
  };

  // 根據 isBiomass 設定 row 的 class
  const rowClassName = (rowData) => {
    return rowData.isBiomass === "Y" ? "is-biomass" : "";
  };

  const toggleExpandAll = () => {
    if (isAllExpanded) {
      setExpandedRows(null);
      setIsAllExpanded(false);
    } else {
      setExpandedRows(Array.isArray(emissionList) ? [...emissionList] : []);
      setIsAllExpanded(true);
    }
  };

  const handleCalculationAuditLog = (rowData) => {
    setCalculationAuditFlag(true);
    setCalculationAuditLogList(rowData.calculationAuditLogList);
  };

  const operationBodyTemplate = (value) => {
    switch (value) {
      case "add":
        return <FontAwesomeIcon icon="fa-solid fa-plus" />;
      case "subtract":
        return <FontAwesomeIcon icon="fa-solid fa-minus" />;
      case "multiply":
        return <FontAwesomeIcon icon="fa-solid fa-multiply" />;
      case "divide":
        return <FontAwesomeIcon icon="fa-solid fa-divide" />;
      default:
        return <FontAwesomeIcon icon="fa-solid fa-equals" />;
    }
  };

  const footerTemplate = () => {
    return (
      <div className="flex justify-content-between flex-wrap align-items-center">
        <span>
          {t("row_count")}: {emissionList?.length}
        </span>
        <div className="flex flex-column text-right" style={{ marginLeft: "auto" }}>
          <label>
            {t("totalEmission")}: {handleTotalEmission(totalEmission, "totalEmission")}
          </label>
          <label className="text-green-700">
            {t("biogenicEmissions")}: {handleTotalEmission(bioEnergyEmission, "bioEnergyEmission")}
          </label>
        </div>
      </div>
    );
  };

  const approveStatusBodyTemplate = (status) => {
    const colorMap = {
      D: "var(--gray-400)",
      C: "var(--green-700)",
      P: "var(--yellow-300)",
      RTA: "var(--red-600)",
    };
    const textColorMap = {
      P: "var(--yellow-800)",
    };

    if (status) {
      return (
        <span
          className={`p-tag`}
          style={{
            background: colorMap[status] || "var(--gray-300)",
            color: textColorMap[status] || "#fff",
          }}
        >
          {t(`approve.${status}`)}
        </span>
      );
    }
  };

  const tableColumn = useMemo(
    () => [
      ...(approveFlag
        ? [
            {
              accessorKey: "status",
              label: "",
              header: "",
              size: 100,
              enableResizing: false,
              enableSorting: false,
              enableColumnFilter: false,
              enableGrouping: false,
              cell: ({ row }) => <div>{approveStatusBodyTemplate(row.original.status)}</div>,
              filterFn: "multiSelect",
              meta: {
                filterOptions: Array.from(new Set(emissionList.map((item) => item.status))),
              },
            },
          ]
        : []),
      {
        accessorKey: "itemNo",
        label: t("itemNo"),
        header: ({ table }) => (
          <div
            onClick={() => {
              table.toggleAllRowsExpanded();
            }}
            style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
          >
            <i
              className="pi pi-angle-double-right"
              style={{
                display: "inline-block",
                transition: "transform 0.2s ease",
                transform: table.getIsAllRowsExpanded() ? "rotate(90deg)" : "rotate(0deg)",
                transformOrigin: "center",
              }}
            />
            <span style={{ marginLeft: "0.5rem" }}>{t("itemNo")}</span>
          </div>
        ),
        size: 50,
        enableSorting: false,
        enableColumnFilter: false,
        enableGrouping: false,
        cell: ({ row }) =>
          row.getCanExpand() ? (
            <div style={{ display: "inline-block", cursor: "pointer" }} onClick={row.getToggleExpandedHandler()}>
              <i
                className={"pi pi-angle-right"}
                style={{
                  display: "inline-block",
                  transition: "transform 0.2s ease",
                  transform: row.getIsExpanded() ? "rotate(90deg)" : "rotate(0deg)",
                }}
              />
              <span style={{ marginLeft: "0.5rem" }}>{row.index + 1}</span>
            </div>
          ) : (
            row.index + 1
          ),
      },
      {
        accessorKey: "displayClassName",
        label: t("category"),
        header: t("category"),
        size: 200,
        rowGroup: true,
        cell: (info) => info.getValue(),
        filterFn: "multiSelect",
        meta: {
          filterOptions: Array.from(new Set(emissionList.map((item) => item.displayClassName))),
        },
      },
      {
        accessorKey: "typeName",
        label: t("emission_source_type"),
        header: t("emission_source_type"),
        size: 200,
        rowGroup: true,
        cell: (info) => info.getValue(),
        filterFn: "multiSelect",
        meta: {
          filterOptions: Array.from(new Set(emissionList.map((item) => item.typeName))),
        },
      },
      {
        accessorKey: "deviceName",
        label: t("equipment"),
        header: t("equipment"),
        size: 200,
        rowGroup: true,
        cell: (info) => info.getValue(),
        filterFn: "multiSelect",
        meta: {
          filterOptions: Array.from(new Set(emissionList.map((item) => item.deviceName))),
        },
      },
      {
        accessorKey: "sourceName",
        label: t("emission_source"),
        header: t("emission_source"),
        size: 200,
        rowGroup: true,
        cell: (info) => info.getValue(),
        filterFn: "multiSelect",
        meta: {
          filterOptions: Array.from(new Set(emissionList.map((item) => item.sourceName))),
        },
      },
      {
        accessorKey: "subtotalAmount",
        label: t("subtotal_amount"),
        header: t("subtotal_amount"),
        size: 180,
        enableGrouping: false,
        enableSubtotal: true,
        headerClass: "text-right",
        cell: (info) => <div style={{ textAlign: "right" }}>{formatWithCommastTemplate(info.getValue())}</div>,
        filterFn: "number",
        aggregatedCell: ({ getValue }) => formatWithCommastTemplate(getValue()),
      },
      {
        accessorKey: "unitName",
        label: t("unit"),
        header: t("unit"),
        size: 150,
        cell: (info) => info.getValue(),
        filterFn: "multiSelect",
        meta: {
          filterOptions: Array.from(new Set(emissionList.map((item) => item.unitName))),
        },
      },
      {
        accessorKey: "subtotalEmission",
        label: t("emissions"),
        header: t("emissions"),
        headerClass: "text-right",
        size: 180,
        enableGrouping: false,
        enableSubtotal: true,
        cell: ({ row }) => <div style={{ textAlign: "right" }}>{subTotalEmissionTemplate(row.original)}</div>,
        filterFn: "number",
        aggregatedCell: ({ getValue }) => formatWithCommastTemplate(getValue()),
      },
      {
        accessorKey: "action",
        label: t("action"),
        header: t("action"),
        size: 200,
        headerClass: "no-print",
        bodyClass: "no-print",
        enableResizing: false,
        enableSorting: false,
        enableColumnFilter: false,
        enableGrouping: false,
        className: "no-print",
        cell: ({ row }) => actionBodyTemplate(row.original),
      },
    ],
    [approveFlag],
  );

  const axios_initDataProp = () => {
    initDataProp({ reqType: "calculationTable" }).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res?.data?.RespBody;
        let flag = data?.calculationTableFlag === "true" ? true : false;
        setNewTableFlag(flag);
      }
    });
  };

  return (
    <>
      {newTableFlag ? (
        <MainDataTable
          key={approveFlag ? "approve" : "normal"}
          tableColumn={tableColumn}
          printFileName={
            itemObj.year +
            "_" +
            itemObj.displayCause +
            "_" +
            itemObj.companyName +
            "_" +
            itemObj.placeName +
            "_" +
            t("Emissions_calculation")
          }
          rowData={emissionList}
          rowClassName={rowClassName}
          rowExpansionTemplate={rowExpansionTemplate}
          footerTemplate={footerTemplate}
          groupTemplate={groupTemplate}
          maxHeight="45rem"
          planPlaceSeq={planPlaceSeq}
          setPlanPlaceSeq={setPlanPlaceSeq}
          companyPlaceList={companyPlaceList}
          approveFlag={approveFlag}
          approveStatus={approveStatus}
          setApproveStatus={setApproveStatus}
          approveStatusList={approveStatusList}
        />
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: ".5rem", transform: "translateY(-1rem)" }}>
            {approveFlag && (
              <div className="flex" style={{ width: "180px" }}>
                <Dropdown
                  showClear
                  value={approveStatus}
                  options={approveStatusList}
                  placeholder={t("plz_chs_all")}
                  className="w-full"
                  onChange={(e) => {
                    setApproveStatus(e.value);
                  }}
                />
              </div>
            )}
            <div className="flex-1">
              <Dropdown
                showClear
                optionLabel="placeName"
                optionValue="planPlaceSeq"
                value={planPlaceSeq}
                options={companyPlaceList}
                placeholder={t("plz_chs_all")}
                className="w-full"
                onChange={(e) => {
                  setPlanPlaceSeq(e.value);
                }}
              />
            </div>
            {/* 全部展開/收合 */}
            <Button
              label={`${t("plz_chs_all")}${isAllExpanded ? t("button.collapse") : t("button.expansion")}`}
              icon={isAllExpanded ? "pi pi-angle-double-up" : "pi pi-angle-double-down"}
              size="small"
              severity="secondary"
              outlined
              onClick={toggleExpandAll}
            />
            {/* 列印 */}
            <Button
              tooltipOptions={{ position: "top" }}
              tooltip={t("button.print")}
              rounded
              text
              severity="secondary"
              className="p-button-icon-only"
              onClick={reactToPrintFn}
            >
              <FontAwesomeIcon icon="fa-solid fa-print" />
            </Button>
          </div>
          <div ref={contentRef}>
            <DataTable
              stripedRows
              className="white-data-table no-shadow"
              rowClassName={rowClassName}
              footer={footerTemplate}
              value={emissionList}
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data)}
              rowExpansionTemplate={rowExpansionTemplate}
              emptyMessage={t("no_rec")}
              scrollable
              {...(emissionList?.length > 20
                ? {
                    resizableColumns: true,
                    virtualScrollerOptions: { itemSize: 73 },
                  }
                : {})}
              {...(!isPrinting && { scrollHeight: "35rem" })}
            >
              <Column
                expander={allowExpansion}
                style={{ width: "80px" }}
                bodyStyle={{ maxWidth: "80px" }}
                className="no-print"
                headerClassName="no-print"
              />
              {approveFlag && (
                <Column
                  style={{ width: "100px" }}
                  bodyStyle={{ maxWidth: "100px" }}
                  header={t("status")}
                  body={(rowData) => approveStatusBodyTemplate(rowData?.status)}
                />
              )}
              <Column
                style={{ width: "150px" }}
                bodyStyle={{ maxWidth: "150px" }}
                header={t("itemNo")}
                body={(rowData, { rowIndex }) => <span>{rowIndex + 1}</span>}
              />
              <Column
                style={{ width: "150px" }}
                bodyStyle={{ maxWidth: "150px" }}
                field="displayClassName"
                header={t("category")}
              />
              <Column
                style={{ width: "150px" }}
                bodyStyle={{ maxWidth: "150px" }}
                field="typeName"
                header={t("emission_source_type")}
              />
              <Column
                style={{ width: "150px" }}
                bodyStyle={{ maxWidth: "150px" }}
                field="deviceName"
                header={t("equipment")}
              />
              <Column
                style={{ minWidth: "150px" }}
                bodyStyle={{ maxWidth: "150px" }}
                field="sourceName"
                header={t("emission_source")}
              />
              <Column
                style={{ width: "150px", textAlign: "right" }}
                bodyStyle={{ maxWidth: "150px" }}
                field="subtotalAmount"
                body={(rowData) => formatWithCommastTemplate(rowData.subtotalAmount)}
                header={t("subtotal_amount")}
                headerStyle={{ textAlign: "left" }}
              />
              <Column
                style={{ width: "150px" }}
                bodyStyle={{ maxWidth: "150px" }}
                field="unitName"
                header={t("unit")}
              />
              <Column
                style={{ width: "150px", textAlign: "right" }}
                bodyStyle={{ maxWidth: "150px" }}
                field="subtotalEmission"
                body={subTotalEmissionTemplate}
                header={t("emissions")}
                headerStyle={{ textAlign: "left" }}
              />
              {/* Not needed temporarily */}
              {/* <Column style={{ minWidth: '80px' }} field="customEmission" header={t('custom_emission')} /> */}
              <Column
                style={{ width: "150px" }}
                bodyStyle={{ maxWidth: "150px" }}
                header={t("action")}
                body={actionBodyTemplate}
                className="no-print"
                headerClassName="no-print"
              />
            </DataTable>
          </div>
        </>
      )}

      {/* 手動計算 Dialog */}
      <PrimeDialog
        isShowDelBtn={calStatus === "update" && cusDisable}
        onDelete={() => {
          handleDelCusEmission();
        }}
        visible={cusDialog}
        onClose={() => {
          setCusDialog(false);
          handleResetCusData();
        }}
        disabledConfirm={!cusDisable || !isValid || !isEditable}
        onConfirm={() => {
          confirmCusReg();
        }}
        title={`${t("custom_calculation")} (${
          !cusDisable ? t("button.readonly") : calStatus === "create" ? t("button.create") : t("button.edit")
        })`}
        bodyTemplate={
          <AddCustomEmission
            register={register}
            errors={errors}
            control={control}
            rateTypeList={rateTypeList}
            fileInputRef={fileInputRef}
            setValue={setValue}
            getValues={getValues}
            cusObj={cusObj}
            watch={watch}
            disabled={!cusDisable}
          />
        }
      />
      {/* 計算過程 */}
      <PrimeDialog
        visible={calculationAuditFlag}
        onClose={() => {
          setCalculationAuditFlag(false);
        }}
        draggable={true}
        resizable={true}
        isShowConfBtn={false}
        isShowDelBtn={false}
        title={`${t("calculationProcess")}`}
        bodyTemplate={
          <DataTable
            className="white-data-table hover-highlight no-shadow"
            value={calculationAuditLogList}
            emptyMessage={t("no_rec")}
            rowGroupMode="rowspan"
            groupRowsBy={["period", "gasCode"]}
          >
            <Column field="period" header={t("period")} style={{ minWidth: "5rem" }} />
            <Column field="gasCode" header={t("gasCode")} style={{ minWidth: "5rem" }} />
            <Column field="valueA" style={{ minWidth: "5rem" }} />
            <Column
              field="operation"
              body={(rowData) => operationBodyTemplate(rowData?.operation)}
              style={{ minWidth: "5rem" }}
            />
            <Column field="valueB" style={{ minWidth: "5rem" }} />
            <Column body={(rowData) => operationBodyTemplate("")} style={{ minWidth: "5rem" }} />
            <Column field="result" header={t("result")} style={{ minWidth: "5rem" }} />
            <Column
              field="remark"
              header={t("remark")}
              body={(rowData) => t(`calculationRemark.${rowData?.remark}`)}
              style={{ minWidth: "8rem" }}
            />
          </DataTable>
        }
      />
    </>
  );
};

export default EmissionsCalculation;
